// src/services/uploadService.js
import apiClient from "./apiClient";
import axios from "axios";
import { BACKEND_URL as ENV_BACKEND_URL, getEnvVar } from "../utils/envConfig";

const API_URL = `/blob`; // Use relative path since apiClient has baseURL

const resolveBackendUrl = () => {
  // Prefer runtime env (window.env / Azure app settings) over module-init defaults
  return (
    getEnvVar("REACT_APP_BACKEND_URL") ||
    getEnvVar("REACT_APP_HMSBACKEND_URL") ||
    ENV_BACKEND_URL ||
    "http://localhost:8080/api"
  );
};

export const isAbsoluteAttachmentUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

/** Azure Blob Storage URLs are private (PublicAccessNotPermitted) — never open directly. */
export const isPrivateAzureBlobUrl = (value) =>
  typeof value === "string" &&
  /blob\.core\.windows\.net/i.test(value.trim());

/** Extract a downloadable filename from a URL or path. */
export const extractAttachmentFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "attachment";
  let filePath = fileName.trim();
  if (filePath.includes("/")) {
    try {
      const url = new URL(filePath);
      const pathParts = url.pathname.split("/").filter(Boolean);
      const filenameWithEncoded = pathParts[pathParts.length - 1] || "attachment";
      filePath = decodeURIComponent(filenameWithEncoded);
    } catch {
      const lastSeparatorIndex = Math.max(
        filePath.lastIndexOf("/"),
        filePath.lastIndexOf("\\")
      );
      if (lastSeparatorIndex >= 0) {
        filePath = decodeURIComponent(filePath.substring(lastSeparatorIndex + 1));
      }
    }
  }
  // Strip query/hash leftovers
  return (filePath.split("?")[0].split("#")[0] || "attachment").trim() || "attachment";
};

/** Trigger a browser file save from a Blob/File. */
export const triggerBrowserFileDownload = (blob, fileName = "attachment") => {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "attachment";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const parseBlobErrorMessage = async (blob, fallback = "File download failed") => {
  if (!(blob instanceof Blob)) return fallback;
  try {
    const text = await blob.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || text || fallback;
    } catch {
      // Azure storage XML error (PublicAccessNotPermitted, etc.)
      const codeMatch = text.match(/<Code>([^<]+)<\/Code>/i);
      const msgMatch = text.match(/<Message>([^<]+)<\/Message>/i);
      if (codeMatch || msgMatch) {
        return [codeMatch?.[1], msgMatch?.[1]].filter(Boolean).join(": ") || fallback;
      }
      return text.length > 300 ? fallback : text;
    }
  } catch {
    return fallback;
  }
};

const assertDownloadBlobIsFile = async (blob, contentType = "") => {
  const type = String(contentType || blob?.type || "").toLowerCase();
  if (
    type.includes("application/json") ||
    type.includes("text/xml") ||
    type.includes("application/xml") ||
    type.includes("text/html") ||
    type.includes("text/plain")
  ) {
    throw new Error(await parseBlobErrorMessage(blob));
  }

  // Some servers omit content-type; sniff small payloads that look like errors
  if (blob instanceof Blob && blob.size > 0 && blob.size < 4096) {
    const head = await blob.slice(0, 256).text();
    if (
      /PublicAccessNotPermitted|<Error>|\"message\"\s*:|\"error\"\s*:/i.test(head)
    ) {
      throw new Error(await parseBlobErrorMessage(blob));
    }
  }
};

export const uploadAttachment = async (file) => {
  try {
    if (!file || !(file instanceof File)) {
      console.error("No valid file selected.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
        }
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }

    if (organizationId && organizationId > 0) {
      formData.append("organizationId", String(organizationId));
    }

    const response = await apiClient.post(`${API_URL}/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const data = response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data && typeof data === "object") return data;
    return data;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Download attachment for the current browser user via backend API.
 * Azure Blob URLs are private (PublicAccessNotPermitted) — never open them directly.
 * Always fetch through /blob/downloadFile and save the response in the browser.
 */
export const downloadFile = async (fileName) => {
  const raw = typeof fileName === "string" ? fileName.trim() : "";
  if (!raw) {
    throw new Error("No attachment file specified");
  }

  const downloadName = extractAttachmentFileName(raw);

  // Never open private Azure blob URLs in a new tab — storage blocks public access.
  // Always download via authenticated backend using the blob file name.
  const backendUrl = resolveBackendUrl();
  const token = localStorage.getItem("token");
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    // Send the original value (blob name or full Azure URL). Backend resolves the blob name
    // and streams private blob bytes using the storage connection string.
    response = await axios.post(
      `${backendUrl}/blob/downloadFile`,
      { filePath: raw },
      {
        responseType: "blob",
        headers,
      }
    );
  } catch (error) {
    const errBlob = error?.response?.data;
    if (errBlob instanceof Blob) {
      throw new Error(await parseBlobErrorMessage(errBlob, error?.message || "File download failed"));
    }
    throw error;
  }

  const contentType = String(response.headers?.["content-type"] || "");
  await assertDownloadBlobIsFile(response.data, contentType);

  triggerBrowserFileDownload(response.data, downloadName);
  return {
    status: response.status,
    data: response.data,
    fileName: downloadName,
    mode: "api",
  };
};

export const updateInvoice = async (id, invoice) => {
  try {
    const response = await apiClient.put(`${API_URL}/${id}`, invoice);
    return response.data;
  } catch (error) {
    console.error("Error updating invoice:", error);
    throw error;
  }
};
